<template>
    <div v-if="isInitialising && props.showInitialLoader" class="brand-loader-wrapper">
        <brand-loader />
    </div>
    <div v-else-if="!isInitialising" class="document-viewer" :class="{ 'modal-mode': isModalMode }" ref="scrollRef" :style="{ '--secondary-highlight': secondaryColor || '' }">
        <div class="breadcrumbs-holder" v-if="!props.noBreadcrumb">
            <router-link
                :to="documentListLink"
                class="mobile-link">
                <font-awesome-icon class="arrowLeft" :icon="['fas', 'arrow-left']" /> Back
            </router-link>
            <breadcrumb-with-underlining :breadcrumbItems="breadcrumbItems" class="breadcrumbs" />
        </div>
        <div class="main-content" :class="{ 'card-main': !isModalMode }">
            <report-details
                ref="documentDescriptionRef"
                allow-zoom
                :has-access="pdfDetails.hasAccess"
                :description="pdfDetails.description"
                :number-of-pages="pdfDetails.numberOfPages"
                :publication-date="pdfDetails.publicationDate"
                :authors="pdfDetails.authors"
                :filters="pdfDetails.filters"
                :title="pdfDetails.documentTitle"
                :subtitle="pdfDetails.documentSubtitle"
                :shared-text="sharedText"
                :access-reason="pdfDetails.accessReason"
                :report-id="pdfDetails.documentShortCode"
                report-type="Pdf"
                @make-full-screen="makeFullScreen"
                @zoom-in="zoomIn"
                @zoom-out="zoomOut"
            />
            <div class="report-section" :class="{ 'no-access': !pdfDetails.hasAccess }">
                <pdf-viewer
                    :clear-context-counter="clearContextCounter"
                    :file="pdf"
                    :highlighted-text-location="activeHighlight"
                    :text-items="textItems"
                    :title="pdfDetails.documentTitle"
                    :scroll-ref="scrollRef"
                    :header-height="documentDescriptionRef?.clientHeight || 0"
                    :add-limited-access-page="!props.pdfDetails.hasAccess && !isTemporaryReport"
                    @secondary-color="s => secondaryColor = s"
                    @watermark-colours="s => watermarkColours = s"
                    ref="pdfViewerRef"
                    hideToolbar
                    v-if="pdf"
                />
                <div class="no-access-window" v-if="!pdfDetails.hasAccess && !isTemporaryReport">
                    <no-report-access />
                </div>
            </div>
            <document-comments
                v-if="isModalMode"
                class="modal-comments"
                :document-short-code="pdfDetails.documentShortCode"
                :initial-comment-count="pdfDetails.hasAccess ? pdfDetails.commentCount : 0"
                :locked="!pdfDetails.hasAccess"
                :allow-expand="props.allowCommentsExpand"
            />
        </div>
        <div v-if="!isModalMode && !isMobileView" class="side-content" ref="sideContentRef">
            <div class="transpose-options">
                <primevue-button text class="plain-btn extra-padding grow" @click="download" :disabled="isDownloadDisabled" v-tooltip.left="downloadTooltip">
                    Download
                    <font-awesome-icon :icon="['fas', 'download']" />
                </primevue-button>
                <primevue-button text class="plain-btn extra-padding grow" @click="showShareDocument" :disabled="isShareDisabled" v-tooltip.left="shareTooltip">
                    Share
                    <font-awesome-icon :icon="['fas', 'share-nodes']" />
                </primevue-button>
            </div>
            <div class="gpt-section options ai-card" :class="{ 'chat-expanded': isChatExpanded }" ref="chatTarget">
                <div class="title">
                    Chat with your research
                    <div class="small-text">AI-powered</div>
                </div>
                <div class="transpose-options">
                    <primevue-button text class="plain-btn chat extra-padding" @click="clickAiServicesButton" :disabled="isTemporaryReport" v-tooltip.left="chatWithReportTooltip">
                        Ask a question, or summarise the report
                        <font-awesome-icon :icon="['fas', 'sparkles']" class="ai-icon" />
                    </primevue-button>
                </div>
            </div>
            <div class="options">
                <document-comments
                    :document-short-code="pdfDetails.documentShortCode"
                    :initial-comment-count="pdfDetails.hasAccess ? pdfDetails.commentCount : 0"
                    :locked="!pdfDetails.hasAccess"
                    compact
                />
            </div>
            <div class="options ai-card">
                <div class="title">
                    Transform your research
                    <div class="small-text">AI-powered</div>
                </div>
                <div class="description">
                    Convert your research into engaging videos and podcasts, in a language of your choice.
                </div>
                <div v-tooltip.top="disabledTransposeTooltipText" class="transpose-options">
                    <primevue-button text class="plain-btn extra-padding" :disabled="isTransposeDisabled" @click="openTransposeModal('Video')">
                        Watch as a video
                        <font-awesome-icon
                            :icon="['fas', isTransposeDisabled ? 'lock-keyhole' : 'video']"
                            :class="{ 'lock-icon': isTransposeDisabled }"
                            class="ai-icon"
                        />
                    </primevue-button>
                    <primevue-button text class="plain-btn extra-padding" :disabled="isTransposeDisabled" @click="openTransposeModal('Podcast')">
                        Listen as a podcast
                        <font-awesome-icon
                            :icon="['fas', isTransposeDisabled ? 'lock-keyhole' : 'podcast']"
                            :class="{ 'lock-icon': isTransposeDisabled }"
                            class="ai-icon"
                        />
                    </primevue-button>
                </div>
            </div>
            <div v-if="pdfDetails.relatedDocuments" class="document-info options">
                <div class="title">Related Documents</div>
                <div class="related-documents flex-column-with-gap">
                    <div
                        v-for="(document, index) in relatedDocuments"
                        :key="document.documentShortCode">
                        <router-link
                            :to="{ name: getResearchSectionRouteName(route, deltaGptRoutes.documentView.name), params: { documentShortCode: document.documentShortCode } }"
                            class="document">
                            <report-thumbnail :thumbnail-url="document.thumbnailUrl" :report-type="document.reportType" />
                            <div class="text">
                                <div class="date">
                                    {{ formatPublicationDateForDisplay(document.publicationDate) }}
                                </div>
                                <div class="document-title">
                                    {{ document.documentTitle }}
                                </div>
                            </div>
                        </router-link>
                        <div class="separator" v-if="index !== relatedDocuments!.length - 1" />
                    </div>
                </div>
            </div>
            <div class="options">
                <div class="title">Contact us</div>
                <div class="description">
                    Let us know if you have any questions about this report, or research service.
                </div>
                <our-experts-section :our-experts="ourExperts" />
                <!-- TODO: allow this to be clicked when not logged in, but need to make them input their email if not logged in and hit non-logged in endpoint, otherwise use as usual -->
                <div>
                    <primevue-button
                        text
                        @click="openContactUs"
                        class="plain-btn">
                        Speak to our experts
                        <font-awesome-icon :icon="['fas', 'user']" />
                    </primevue-button>
                </div>
            </div>
        </div>
        <div class="bubbles-holder" v-show="pdf && !chatTargetIsVisible && !shouldHideBubblesOnMobile">
            <primevue-button text class="options-icon scroll-icon plain-btn" @click="download" :disabled="isDownloadDisabled" v-tooltip.left="downloadTooltip">
                <font-awesome-icon :icon="['fas', 'download']" />
            </primevue-button>
            <primevue-button text class="options-icon scroll-icon plain-btn" v-tooltip.left="shareTooltip" @click="showShareSlideModal" :disabled="isShareDisabled">
                <font-awesome-icon :icon="['fas', 'share-nodes']" />
            </primevue-button>
            <div class="scroll-to-top">
                <primevue-button text class="options-icon scroll-icon plain-btn" v-tooltip.left="'Scroll to top'" @click="scrollToTop">
                    <font-awesome-icon :icon="['fas', 'chevron-up']" />
                </primevue-button>
            </div>
            <primevue-button ref="aiServicesButtonRef" text class="options-icon ai-service-icon plain-btn ai-card" @click="openAiServices" :disabled="isTemporaryReport" v-tooltip.left="chatWithReportTooltip">
                Ask a question<font-awesome-icon class="ai-icon" :icon="showAiPopover ? ['fas', 'xmark'] : ['fas', 'sparkles']" />
            </primevue-button>
        </div>
        <central-modal
            v-model:visible="showTransposeModal"
            header="Context Details"
            @hide="closeTransposeModal">
            <div class="tranpose-config" v-if="!showIFrame && formattedTransposeOptions">
                I want a&nbsp;
                <div>
                    <primevue-select
                        v-model="transposeContextDetail"
                        :options="formattedTransposeOptions[contextType as keyof IFormattedTransposeOption].contextDetail"
                        placeholder="Select a context"
                        option-label="name"
                        option-value="id"
                    />
                    <info-icon-with-tooltip
                        :tooltip-text="infoIconText"
                    />
                </div>

                {{ contextType }}&nbsp;in&nbsp;
                <primevue-select
                    v-model="transposeLanguage"
                    :options="formattedTransposeOptions[contextType as keyof IFormattedTransposeOption].language"
                    placeholder="Select a language" />
            </div>
            <div v-else :style="{ width: '80vw', height: '80vh' }" id="transpose-wrapper" />
            <template #footer>
                <primevue-button v-if="!showIFrame" class="plain-btn" @click="createTransposeView">
                    Create
                    <font-awesome-icon :icon="['fas', 'arrow-right']" />
                </primevue-button>
            </template>
        </central-modal>
        <contact-us
            v-model:visible="showContactUsModal"
            :documentTitle="pdfDetails.documentTitle"
            :our-experts="ourExperts"
        />
        <primevue-dialog
            :draggable="false"
            :style="{
                width: `${aiServiceWidth}px`, height: '80vh', right: '3rem', position: 'fixed',
            }"
            v-model:visible="showAiPopover"
            @show="setScrollTracking(true)"
            @hide="setScrollTracking(false)"
        >
            <chat-panel
                class="panel-popover"
                isDocumentView
                :can-clear-context="!isReplying"
                :can-expand-chat="false"
                :is-awaiting-response="isAwaitingResponse"
                :is-chat-expanded="true"
                :is-replying="isReplying"
                :messages="messages"
                :conversation-history="conversationHistory"
                @clear-context="clearContext"
                @send-message="sendMessage"
                @source-clicked="sourceClicked"
                @toggle-chat-expanded="toggleChatExpanded"
            >
                <template #prompts>
                    <chat-prompt
                        :key="index + prompt.prompt"
                        :prompt="prompt"
                        @send-message="sendMessage"
                        v-for="(prompt, index) in formattedPrompts"
                    />
                </template>
            </chat-panel>
        </primevue-dialog>
        <central-modal
            v-model:visible="showShareModal"
            header="Share Document"
            size="small"
        >
            <share-document :pdf-details="props.pdfDetails" :initial-slide-number="currentPageNumber" :pdf="pdf" v-if="pdf" />
        </central-modal>
        <central-modal
            v-model:visible="showTemporaryReportExplanationModal"
            header="Temporary Report Access"
            size="small"
        >
            <div class="temp-report-content">
                <div v-if="isLcpUser" v-html="temporaryReportExplanationText" />
                <div v-else class="temp-explanation-holder">
                    <div class="left">
                        <div class="title">
                            Welcome to Spark!
                        </div>
                        <div>
                            This report has been made temporarily available to you until <strong>{{props.sharedDocumentInfo!.expiryTime}}.</strong>
                        </div>
                        <div>
                            After that, you will no longer have access to this report.
                        </div>
                        <div>
                            You can also read our public reports at <a :href="researchUrl" target="_blank" rel="noopener noreferrer">{{ researchUrl }}</a>.
                        </div>
                        <div>
                            Please contact <a href="mailto:${props.sharedDocumentInfo!.sharedBy}">{{props.sharedDocumentInfo!.sharedBy}}</a> if you have any questions.
                        </div>
                    </div>
                    <div class="right">
                        <div>
                            <img class="spark-logo" :src="sparkLogo" alt="spark logo" />
                        </div>
                    </div>
                </div>
            </div>
        </central-modal>
    </div>
</template>

<script lang="ts" setup>

import {
    ref, shallowRef, computed, nextTick, watch, useTemplateRef, onUnmounted,
} from 'vue';
import type { TextItem } from 'pdfjs-dist/types/src/display/api.d.ts';
import PrimevueButton from 'primevue/button';
import { Transpose, IGenerationOption } from '@lcp/transpose';
import PrimevueDialog from 'primevue/dialog';
import CentralModal from '@/components/common/CentralModal.vue';
import PrimevueSelect from 'primevue/select';
import { useDeltaGptStore } from '@/stores/deltaGpt/deltaGptStore.ts';
import PdfViewer from '@/components/deltaGpt/PdfViewer.vue';
import { IChatMessage } from '@/interfaces/deltaGpt/IChatMessage.ts';
import { IPdfDetails, IDocumentSimple } from '@/interfaces/deltaGpt/IDocument.ts';
import ChatPanel from '@/components/deltaGpt/ChatPanel.vue';
import ChatPrompt from '@/components/deltaGpt/ChatPrompt.vue';
import { deltaGptApi } from '@/api/deltaGpt/deltaGptApi.ts';
import { publishToast } from '@/utils/Toast.ts';
import { useToast } from 'primevue/usetoast';
import { IExpert } from '@/interfaces/deltaGpt/IExpert.ts';
import { useDeltaGptHistoryStore } from '@/stores/deltaGpt/deltaGptHistoryStore.ts';
import { useConversationHistory } from '@/composables/deltaGPT/useConversationHistory.ts';
import BreadcrumbWithUnderlining from '@/components/common/BreadcrumbWithUnderlining.vue';
import { useElementVisibility, useWindowSize } from '@vueuse/core';
import { IBreadcrumbItem } from '@/interfaces/general/IBreadcrumbItem.ts';
import { createDocumentBreadcrumbItems } from '@/utils/documentBreadcrumbUtils.ts';
import { authState } from '@lcp/http-auth';
import { useSeoMeta } from '@unhead/vue';
import { defineArticle, useSchemaOrg } from '@unhead/schema-org/vue';
import { IIdName } from '@/interfaces/general/IIdName.ts';
import InfoIconWithTooltip from '@/components/common/InfoIconWithTooltip.vue';
import { deltaGptRoutes } from '@/routes/deltaGptRoutes.ts';
import { useUserAccessStore } from '@/stores/userAccessStore.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { addWatermarkToPdf } from '@/helpers/pdf/addWatermarkToPdf.ts';
import { ISharedDocumentInfo } from '@/interfaces/deltaGpt/ISharedDocumentInfo.ts';
import sparkLogo from '@/assets/imgs/svgs/spark-logo.svg';
import { getResearchSectionRouteName } from '@/routes/routeUtils.ts';
import { useRoute, useRouter } from 'vue-router';
import { SCREEN_SIZES } from '@/constants/ScreenSizes.ts';
import { getNames } from '@/utils/documentFiltersUtils.ts';
import { IPrompt } from '@/interfaces/deltaGpt/IPrompt.ts';
import { SPARK_DEFAULT_OG_IMAGE_URL, SPARK_SITE_URL } from '@/constants/SparkSite.ts';
import BrandLoader from '@/components/layout/BrandLoader.vue';
import { downloadBlob } from '@/utils/downloadUtils.ts';
import { formatPublicationDateForDisplay } from '@/utils/sparkContentUtils.ts';
import ContactUs from './ContactUs.vue';
import OurExpertsSection from './OurExperts.vue';
import ShareDocument from './ShareDocument.vue';
import ReportDetails from './ReportDetails.vue';
import DocumentComments from './DocumentComments.vue';
import NoReportAccess from './NoReportAccess.vue';
import ReportThumbnail from '../sectionComponents/ReportThumbnail.vue';

let transposeInstance: Transpose;
const transposeOptions = ref<Array<IGenerationOption>>([]);

interface IFormattedTransposeOption {
    Video: {
        language: Array<string>;
        contextDetail: Array<IIdName>;
    };
    Podcast: {
        language: Array<string>;
        contextDetail: Array<IIdName>;
    };
}

type TransposeMediaType = keyof IFormattedTransposeOption;

const props = withDefaults(defineProps<{
    pdfDetails: IPdfDetails;
    shareId?: string;
    sharedDocumentInfo? : ISharedDocumentInfo | null;
    noBreadcrumb?: boolean;
    displayMode?: 'page' | 'modal';
    allowCommentsExpand?: boolean;
    showInitialLoader?: boolean;
}>(), {
    shareId: undefined,
    sharedDocumentInfo: null,
    noBreadcrumb: false,
    displayMode: 'page',
    allowCommentsExpand: true,
    showInitialLoader: true,
});

const emit = defineEmits<{
    initialised: [];
}>();

const deltaGptStore = useDeltaGptStore();
const toast = useToast();
const deltaGptHistoryStore = useDeltaGptHistoryStore();
const userAccessStore = useUserAccessStore();
const userSettingsStore = useUserSettingsStore();
const route = useRoute();
const router = useRouter();
const { width } = useWindowSize();

const pdf = shallowRef<Uint8Array>();
const watermarkColours = ref<Array<string | null>>([]);
const watermarkedPdf = shallowRef<Uint8Array | null>(null);
const isPreparingDownload = ref<boolean>(false);
const relatedDocuments = ref<Array<IDocumentSimple>>([]);
const textItems = shallowRef<Record<string, Array<TextItem>>>({});
const messages = ref<Array<IChatMessage>>([]);
const isAwaitingResponse = ref<boolean>(false);
const isReplying = ref<boolean>(false);
const activeHighlight = ref<string>('');
const isChatExpanded = ref<boolean>(false);
const clearContextCounter = ref<number>(0);
const showTransposeModal = ref<boolean>(false);
const showContactUsModal = ref<boolean>(false);
const showIFrame = ref<boolean>(false);
const contextType = ref<TransposeMediaType>('Video');
const transposeLanguage = ref<string>('');
const transposeContextDetail = ref<string>('');
const ourExperts = ref<Array<IExpert>>([]);
const showAiPopover = ref<boolean>(false);
const aiServiceWidth = ref<number>(0);
const showShareModal = ref<boolean>(false);
const showTemporaryReportExplanationModal = ref<boolean>(false);
const isChatBelowViewport = ref<boolean>(false);
const isInitialising = ref<boolean>(false);

const currentPageNumber = ref<number | undefined>();

const documentDescriptionRef = ref<HTMLElement | null>(null);
const scrollRef = ref<HTMLDivElement | null>(null);
const pdfViewerRef = ref<typeof PdfViewer | null>(null);
const sideContentRef = ref<HTMLDivElement | null>(null);
const aiServicesButtonRef = ref<{ $el: HTMLElement } | null>(null);

const secondaryColor = ref<string | null>(null);
const savedScrollPosition = ref<number>(0);

const updateScrollPosition = (): void => {
    if (scrollRef.value) {
        savedScrollPosition.value = scrollRef.value.scrollTop;
    }
};

const isTemporaryReport = computed<boolean>(() => !!props.shareId);
const isModalMode = computed<boolean>(() => props.displayMode === 'modal');

const chatWithReportTooltip = computed<string>(() => {
    if (!isTemporaryReport.value) return '';
    return 'Unable to chat with temporarily available report';
});

const sharedText = computed<string | undefined>(() => {
    if (!isTemporaryReport.value) return undefined;
    let text = `This report has been shared to you by ${props.sharedDocumentInfo?.sharedBy}.`;
    if (props.sharedDocumentInfo?.expiryTime) {
        text += ` Your access to this report will expire at ${props.sharedDocumentInfo?.expiryTime}.`;
    }
    return text;
});

const researchUrl = `${window.location.origin}/research`;

const isLcpUser = computed<boolean>(() => userAccessStore.userAccess.isLcp);

const chatTarget = useTemplateRef<HTMLDivElement>('chatTarget');
const chatTargetIsVisible = useElementVisibility(chatTarget);

const shouldHideBubblesOnMobile = computed<boolean>(() => {
    if (!isMobileView.value) return false;
    return isChatBelowViewport.value;
});

const {
    conversationHistory,
    previousConversationMessages,
    redirectFromHistory,
} = useConversationHistory();

const isMobileView = computed(() => width.value <= SCREEN_SIZES.TABLET_MAX);
const infoIconText = computed<string>(() => `This determines the length/detail of the ${contextType.value.toLocaleLowerCase()}`);
const isTransposeDisabled = computed<boolean>(() => !props.pdfDetails.hasAccess || !transposeOptions.value.length || isTemporaryReport.value);
const disabledTransposeTooltipText = computed<string>(() => (isTransposeDisabled.value ? 'You do not have access to AI services for this document, please contact us to request access.' : ''));

const isDownloadDisabled = computed<boolean>(() => !props.pdfDetails.hasAccess || isTemporaryReport.value);
const downloadTooltip = computed<string>(() => {
    if (!isDownloadDisabled.value) return '';
    if (!props.pdfDetails.hasAccess) return 'No access to download report';
    return 'Unable to download temporarily available report';
});

const isShareDisabled = computed<boolean>(() => !props.pdfDetails.hasAccess || isTemporaryReport.value);
const shareTooltip = computed<string>(() => {
    if (!isShareDisabled.value) return 'Share slide';
    if (!props.pdfDetails.hasAccess) return 'No access to share report';
    return 'Unable to share temporarily available report';
});

const temporaryReportExplanationText = computed<string>(() => {
    if (isLcpUser.value) {
        const availabilityText = props.sharedDocumentInfo!.expiryTime ? `The report has been opened by a non-LCPer, and so will expire at ${props.sharedDocumentInfo!.expiryTime}` : `Since you are an LCP Delta user, the ${props.sharedDocumentInfo!.numberOfHours} hour expiry has not started yet, but will do once a non-LCP user opens this link.`;
        return '<b>WARNING - you are currently accessing this report via a temporary link.</b>'
        + `<br><br>This report is temporarily available for anyone, for ${props.sharedDocumentInfo!.numberOfHours} hours. <br><br> ${availabilityText}`
        + `<br><br>This report was shared by ${props.sharedDocumentInfo!.sharedBy}`;
    }
    return `<strong>Welcome to Spark!</strong><br><br> This report has been made temporarily available to you until <b>${props.sharedDocumentInfo!.expiryTime}</b> - after that, you will no longer have access to this report.<br><br>`
    + `You can also read our public reports at <a href="${researchUrl}" target="_blank" rel="noopener noreferrer">${researchUrl}</a>. <br><br>`
    + `Please contact <a href="mailto:${props.sharedDocumentInfo!.sharedBy}">${props.sharedDocumentInfo!.sharedBy}</a> if you have any questions.`;
});

const documentListLink = computed(() => ({
    name: getResearchSectionRouteName(route, deltaGptRoutes.documentList.name),
    query: route.query,
}));

const breadcrumbItems = computed<Array<IBreadcrumbItem>>(() => createDocumentBreadcrumbItems(
    props.pdfDetails.documentTitle,
    props.pdfDetails.documentShortCode,
    (): void => {
        router.push(documentListLink.value);
    },
));

const formattedPrompts = computed<Array<IPrompt>>(() => {
    const questions = props.pdfDetails.questions;

    return questions
        .map((prompt) => ({ prompt }))
        .concat([{ prompt: 'Summarise this report' }]);
});

function download (): void {
    getWatermarkedPdf().then((downloadBytes) => {
        if (!downloadBytes) return;
        downloadBlob(new Blob([downloadBytes], { type: 'application/pdf' }), `${props.pdfDetails.documentTitle}.pdf`);
    }).catch(error => {
        publishToast(toast, error.message, 'error');
    });
    deltaGptApi.logDocumentInteraction(props.pdfDetails.documentShortCode);
}

function makeFullScreen (): void {
    if (!pdfViewerRef.value) return;
    pdfViewerRef.value.openFullScreen();
}

function zoomIn (): void {
    if (!pdfViewerRef.value) return;
    pdfViewerRef.value.zoomIn();
}

function zoomOut (): void {
    if (!pdfViewerRef.value) return;
    pdfViewerRef.value.zoomOut();
}

function showShareDocument (): void {
    currentPageNumber.value = undefined;
    showShareModal.value = true;
}

function showShareSlideModal (): void {
    if (!pdfViewerRef.value) return;
    currentPageNumber.value = pdfViewerRef.value.getCurrentPageNumber();
    showShareModal.value = true;
}

function clickAiServicesButton (): void {
    const buttonContext = aiServicesButtonRef.value?.$el;
    if (!buttonContext) return;
    buttonContext.focus();
    buttonContext.click();
}

function openAiServices (): void {
    aiServiceWidth.value = sideContentRef.value ? sideContentRef.value.clientWidth - 22 : 200;
    showAiPopover.value = true;
}

function scrollToTop (): void {
    if (!scrollRef.value) return;
    scrollRef.value.scrollTo({ top: 0, behavior: 'smooth' });
}

function saveCurrentScrollPosition (): void {
    if (!scrollRef.value) return;
    savedScrollPosition.value = scrollRef.value.scrollTop;
}

function attachScrollTracker (): void {
    if (!scrollRef.value) return;
    scrollRef.value.addEventListener('scroll', updateScrollPosition);
}

function detachScrollTracker (): void {
    if (!scrollRef.value) return;
    scrollRef.value.removeEventListener('scroll', updateScrollPosition);
}

function restoreScrollPosition (): void {
    nextTick(() => {
        if (!scrollRef.value) return;
        scrollRef.value.scrollTop = savedScrollPosition.value;
    });
}

function sourceClicked (source: string): void {
    activeHighlight.value = source;
    if (!isMobileView.value) return;
    showAiPopover.value = false;
}

function toggleChatExpanded (): void {
    isChatExpanded.value = !isChatExpanded.value;
}

function clearContext (): void {
    deltaGptHistoryStore.currentChatHistoryId = null;
    deltaGptHistoryStore.currentChatMessageHistory = [];
    messages.value = [];
    clearContextCounter.value += 1;
}

async function sendMessage (message: string): Promise<void> {
    if (messages.value.length === 0) deltaGptHistoryStore.currentChatHistoryId = null;
    const conversationId = deltaGptHistoryStore.currentChatHistoryId ?? crypto.randomUUID();
    deltaGptHistoryStore.currentChatHistoryId = conversationId;
    isReplying.value = true;
    isAwaitingResponse.value = true;
    messages.value.push({ content: message, role: 'user' });
    await deltaGptStore.fetchDocumentChat(props.pdfDetails.documentShortCode, conversationId, messages.value, (response) => {
        if (isAwaitingResponse.value) {
            messages.value.push({ content: '', role: 'assistant' });
            isAwaitingResponse.value = false;
        }
        messages.value.at(-1)!.content += response;
    }).catch(error => {
        publishToast(toast, error.message, 'error');
    });

    isReplying.value = false;
}

const fileId = ref<string | null>(null);
const clientId = ref<string | null>(null);
const bearerToken = ref<string | null>(null);

const formattedTransposeOptions = ref<IFormattedTransposeOption | null>(null);

function createFormattedTransposeOptions (): void {
    if (!transposeOptions.value) return;

    formattedTransposeOptions.value = {
        Video: {
            language: [...new Set(transposeOptions.value.filter((option) => option.generationType === 'Video').map((option) => option.language))],
            contextDetail: getUniqueContextDetail('Video'),
        },
        Podcast: {
            language: [...new Set(transposeOptions.value.filter((option) => option.generationType === 'Podcast').map((option) => option.language))],
            contextDetail: getUniqueContextDetail('Podcast'),
        },
    };
}

function getUniqueContextDetail (generationType: string): Array<IIdName> {
    const filteredOptions = transposeOptions.value.filter(
        option => option.generationType === generationType,
    );

    return Array.from(
        new Set(filteredOptions.map(o => o.contentDetail)),
    ).map(id => (
        {
            id,
            name: id === 'Balanced' ? 'Standard' : id,
        }
    ));
}

function openTransposeModal (type: TransposeMediaType): void {
    contextType.value = type;
    if (formattedTransposeOptions.value) {
        transposeLanguage.value = formattedTransposeOptions.value[contextType.value].language[0];
        transposeContextDetail.value = (formattedTransposeOptions.value[contextType.value].contextDetail[0]).id;
    }
    showTransposeModal.value = true;
}

function closeTransposeModal (): void {
    showIFrame.value = false;
}

function openContactUs (): void {
    showContactUsModal.value = true;
}

function createTransposeView (): void {
    const generationOption = transposeOptions.value.find((option) => option.language === transposeLanguage.value && option.contentDetail === transposeContextDetail.value && option.generationType === contextType.value);
    if (!generationOption) return;
    deltaGptApi.trackTransposeRequestHistory({ ...generationOption, documentShortCode: props.pdfDetails.documentShortCode });
    showIFrame.value = true;
    nextTick(() => {
        if (fileId.value) transposeInstance.createIframe('transpose-wrapper', fileId.value, generationOption.generationOptionId);
    });
}

useSchemaOrg([
    defineArticle({
        id: props.pdfDetails.documentShortCode,
        headline: computed(() => props.pdfDetails.documentTitle),
        description: computed(() => props.pdfDetails.description),
        image: SPARK_DEFAULT_OG_IMAGE_URL, // TODO replace with first slide image
        author: computed(() => props.pdfDetails.authors),
        publisher: ['LCP Delta'],
        datePublished: computed(() => props.pdfDetails.publicationDate),
        keywords: getNames(props.pdfDetails.filters),
        articleSection: ['Energy'],
        type: 'Report',
    }),
]);

useSeoMeta({
    title: computed(() => props.pdfDetails.documentTitle),
    ogTitle: computed(() => props.pdfDetails.documentTitle),
    twitterTitle: computed(() => props.pdfDetails.documentTitle),

    description: computed(() => props.pdfDetails.description),
    ogDescription: computed(() => props.pdfDetails.description),
    twitterDescription: computed(() => props.pdfDetails.description),

    ogUrl: computed(() => `${SPARK_SITE_URL}/research/document/${props.pdfDetails.documentShortCode}`),

    ogImage: computed(() => ({
        url: SPARK_DEFAULT_OG_IMAGE_URL, // TODO replace with first slide image
        width: 1200,
        height: 600,
        alt: props.pdfDetails.documentTitle,
        type: 'image/png',
    })),
    twitterImage: computed(() => ({
        url: SPARK_DEFAULT_OG_IMAGE_URL, // TODO replace with first slide image
        width: 1200,
        height: 600,
        alt: props.pdfDetails.documentTitle,
        type: 'image/png',
    })),

    articleAuthor: computed(() => ['LCP Delta', ...props.pdfDetails.authors]),
    articlePublishedTime: computed(() => props.pdfDetails.publicationDate),
    articleModifiedTime: computed(() => props.pdfDetails.publicationDate),
    articleSection: 'Energy',
    articleTag: computed(() => getNames(props.pdfDetails.filters)),

    twitterCard: 'summary_large_image',
});

async function fetchDocumentData (): Promise<void> {
    relatedDocuments.value = deltaGptStore.allDocumentsSimple.filter((document) => props.pdfDetails.relatedDocuments.includes(document.documentShortCode));
    fileId.value = props.pdfDetails.transposeId;

    const promises: Array<Promise<void>> = [];

    addfetchPdfTask(promises);

    if (authState.value.loggedIn && props.pdfDetails.hasAccess) {
        addDocumentReferenceTask(promises);
        addTransposeDetailsTask(promises);
    }

    addGetExpertsTask(promises);
    await Promise.all(promises);
}

function addGetExpertsTask (promises: Array<Promise<void>>): void {
    // TODO: sort
    promises.push(
        deltaGptApi.getExperts({ subscriptions: [] }).then((result) => {
            ourExperts.value = result;
        }).catch(error => {
            publishToast(toast, error.message, 'error');
        }),
    );
}

async function fetchPdf (): Promise<void> {
    const result = await deltaGptStore.fetchPdf(props.pdfDetails.documentShortCode, props.shareId);
    pdf.value = result;
    watermarkedPdf.value = null;
}

async function getWatermarkedPdf (): Promise<Uint8Array | null> {
    if (watermarkedPdf.value) return watermarkedPdf.value;
    if (!pdf.value || isPreparingDownload.value) return null;

    isPreparingDownload.value = true;
    try {
        watermarkedPdf.value = await addWatermarkToPdf(pdf.value, {
            userEmail: authState.value.userInfo?.username ?? userSettingsStore.userSettings.otherSettings.data?.userEmail ?? '',
            publicationDate: props.pdfDetails.publicationDate,
            textColours: watermarkColours.value,
        });
        return watermarkedPdf.value;
    } finally {
        isPreparingDownload.value = false;
    }
}

function addfetchPdfTask (promises: Array<Promise<void>>): void {
    promises.push(
        fetchPdf().catch(error => {
            publishToast(toast, error.message, 'error');
        }),
    );
}

function addDocumentReferenceTask (promises: Array<Promise<void>>): void {
    promises.push(
        deltaGptStore.fetchDocumentReferences(props.pdfDetails.documentShortCode).then((result) => {
            textItems.value = result;
        }).catch(error => {
            publishToast(toast, error.message, 'error');
        }),
    );
}

function addTransposeDetailsTask (promises: Array<Promise<void>>) : void {
    promises.push(
        deltaGptApi.getTransposeDetails().then((result) => {
            clientId.value = result.clientId;
            bearerToken.value = result.token;

            Transpose.initialise({
                clientId: clientId.value,
                bearerToken: bearerToken.value,
            }).then(async (transpose) => {
                transposeInstance = transpose;

                if (fileId.value) {
                    transpose.fetchFileOptions(fileId.value).then((options) => {
                        transposeOptions.value = options;
                        createFormattedTransposeOptions();
                    }).catch(error => {
                        publishToast(toast, error.message, 'error');
                    });
                }
            });
        }).catch(error => {
            publishToast(toast, error.message, 'error');
        }),
    );
}

function setScrollTracking (visible: boolean): void {
    if (!visible) {
        detachScrollTracker();
        restoreScrollPosition();
        return;
    }

    saveCurrentScrollPosition();
    attachScrollTracker();
}

async function initialise (): Promise<void> {
    isInitialising.value = true;

    try {
        await fetchDocumentData();

        if (redirectFromHistory.value && messages.value.length > 0) {
            openAiServices();
        }
    } finally {
        isInitialising.value = false;
    }

    if (props.shareId) showTemporaryReportExplanationModal.value = true;

    if (scrollRef.value) {
        scrollRef.value.addEventListener('scroll', () => {
            if (!chatTarget.value || !scrollRef.value) return;
            const chatTargetRect = chatTarget.value.getBoundingClientRect();
            isChatBelowViewport.value = chatTargetRect.top < 0;
        });
    }

    await nextTick();
    emit('initialised');
}

initialise();

watch(() => previousConversationMessages.value, (newValue) => {
    messages.value = newValue ?? [];
}, { immediate: true });

watch([pdf, watermarkColours], () => {
    watermarkedPdf.value = null;
    if (!pdf.value || !watermarkColours.value.length || isPreparingDownload.value) return;
    getWatermarkedPdf().catch(error => {
        publishToast(toast, error.message, 'error');
    });
}, { deep: true });

onUnmounted(detachScrollTracker);

</script>

<style>
@property --secondary-highlight {
  syntax: '<color>';
  inherits: true;
  initial-value: transparent;
}
</style>

<style lang="scss" scoped>
@use '/src/assets/scss/components/research/documentView.scss';

.brand-loader-wrapper {
    position: relative;
    height: 100%;
    min-height: 0;
}

#transpose-wrapper {
    border-radius: var(--borderRadiusOuter);
    height: 100%;
    width: 100%;
}

.panel-popover {
    background-color: var(--neutral200);
}

.temp-report-content {
    padding: var(--extraLargeSpacing);
    .temp-explanation-holder {
        display: flex;
        flex-direction: row;
        gap: 5rem;
        .left {
            display: flex;
            flex-direction: column;
            gap: var(--extraLargeSpacing);
            .title {
                font-size: 4rem;
                padding-bottom: var(--extraLargeSpacing);
            }
        }
        .right {
            .spark-logo {
                height: 20rem;
            }
        }
    }
}

.document-viewer {
    height: 100%;
    min-height: 0;

    &.modal-mode {
        grid-template-columns: minmax(0, 1fr);
        grid-template-rows: minmax(0, 1fr);
        grid-template-areas: "main";
        height: 100%;
        min-height: 0;
        overflow-y: auto;

        .main-content {
            align-self: stretch;
            width: 100%;
            margin: 0;
            min-height: 0;
        }
    }
    &:not(.modal-mode) {
        .main-content {
            height: auto;
            margin-bottom: var(--largeSpacing);
            background: linear-gradient(180deg, var(--secondary-highlight), var(--neutral200) 40rem) border-box;
        }
    }

    .main-content {
        align-self: start;
        padding: var(--largeSpacing);
        margin: var(--extraLargeSpacing);
        min-height: 0;
    }
    .bubbles-holder,
    .side-content .bubbles-holder {
            position: absolute;
            right: 3rem;
            max-width: calc(100vw - 6rem);
            bottom: 2rem;
            display: flex;
            gap: var(--largeSpacing);
            z-index: 2;
            svg {
                height: 3rem;
            }
            .options-icon {
                box-shadow: var(--shadow2);
                &.ai-service-icon {
                    border-radius: 9999px;
                    gap: var(--defaultSpacing);
                    white-space: nowrap;
                    padding: var(--largeSpacing) var(--extraLargeSpacing);
                }
                &.scroll-icon {
                    border-radius: 50%;
                    height: 8rem;
                    width: 8rem;
                }
                &:hover {
                    transform: scale(1.02);
                    transition: transform 0.2s ease, background-color 0.2s ease;
                }
            }

        }

    .side-content {
        .transpose-options {
            display: flex;
            justify-content: space-between;
            gap: var(--defaultSpacing);

            .lock-icon {
                color: var(--lcpSunrise);
            }

            .grow {
                flex: 1;
                display: flex;
            }

            .chat {
                flex: 1;
                display: flex;
            }
        }

        .related-documents {
            .separator {
                background: var(--neutral300);
                height: 1px;
                width: 90%;
                margin: 0 auto;
                margin-top: var(--largeSpacing);
            }
            .document {
                display: flex;
                flex-direction: row;
                gap: var(--defaultSpacing);
                align-items: center;
                font-size: var(--smallFontSize);
                background: var(--neutral100);
                margin: 0;
                color: var(--primaryFont);
                &:hover {
                    .document-title {
                        transform: scale(1.02);
                        transition: transform 0.2s ease, background-color 0.2s ease;
                    }
                }

                .text {
                    display: flex;
                    flex: 1;
                    flex-direction: column;
                    justify-content: center;
                    gap: var(--smallSpacing);
                    .date {
                        font-style: italic;
                        font-size: var(--extraSmallFontSize);
                    }
                }
                :deep(.thumbnail-wrapper) {
                    width: 15rem;
                    height: 8rem;
                }
            }
        }
    }

    .modal-comments {
        border-top: 1px solid var(--neutral300);
        margin-top: var(--largeSpacing);
        padding-top: var(--largeSpacing);
    }

    .report-section {
        min-height: 0;

        :deep(.pdf) {
            overflow-y: auto;
            overflow-x: auto;
        }

        .document-info {
            display: grid;

            .buttons {
                display: flex;
                gap: var(--defaultSpacing);
            }
        }
    }

    &:not(.modal-mode) .report-section {
        overflow-y: visible;
    }

    .gpt-section {
        display: flex;
        flex-direction: column;
        grid-area: chat;

        @include responsiveHeight('mdUp') {
            grid-template-rows: 7fr 3fr;
        }

        &.chat-expanded {
            grid-template-rows: 0fr 1fr;
            gap: unset;
            z-index: 10;

            .hidable-content {
                padding: 0;
            }
        }
    }
}

.tranpose-config {
    display: flex;
    gap: var(--defaultSpacing);
    align-items: center;
    justify-content: center;
    flex-direction: row;
    padding: var(--defaultSpacing);

    .info-icon {
        margin: 0;
        padding-left: var(--smallSpacing);
    }

    .p-dropdown {
        width: 200px;
    }
}

.no-access-window {
    margin-top: -80rem;
}
</style>
